/**
 * Component Types Catalog — vida_pet (Next.js 14 / React)
 *
 * IDs match EXACTLY the component names used in vida_pet's COMPONENT_ORDER
 * and renderComponent() switch. Content keys match the real props each
 * component receives via spread ({...content}).
 *
 * Source of truth: vida_pet/src/app/[locale]/[[...segments]]/page.tsx
 * and individual component files in vida_pet/src/components/
 */

export interface ComponentField {
  key: string;
  type: 'text' | 'textarea' | 'image' | 'url' | 'array' | 'object' | 'boolean';
  label: string;
  placeholder?: string;
  itemTemplate?: Record<string, string>;
}

export interface ComponentType {
  id: string;                       // exact id used in vida_pet switch/COMPONENT_ORDER
  label: string;                    // display name for the CMS UI
  category: ComponentCategory;
  icon: string;
  description: string;
  fields: ComponentField[];
  defaultContent: Record<string, unknown>;
  /** If true, component fetches its own content and ignores CMS content */
  selfFetching?: boolean;
  /** Special rendering notes for agents */
  notes?: string;
}

export type ComponentCategory =
  | 'hero'
  | 'text'
  | 'gallery'
  | 'cards'
  | 'articles'
  | 'interactive'
  | 'media'
  | 'layout'
  | 'navigation';

export const COMPONENT_CATEGORIES: Record<ComponentCategory, { label: string; icon: string }> = {
  hero:        { label: 'Hero / Portada',   icon: '🖼️' },
  text:        { label: 'Texto',            icon: '📝' },
  gallery:     { label: 'Galería',          icon: '🖼️' },
  cards:       { label: 'Tarjetas / Grid',  icon: '🃏' },
  articles:    { label: 'Artículos',        icon: '📰' },
  interactive: { label: 'Interactivo',      icon: '⚡' },
  media:       { label: 'Multimedia',       icon: '🎬' },
  layout:      { label: 'Layout / Bloques', icon: '📐' },
  navigation:  { label: 'Navegación',       icon: '🔗' },
};

// ─────────────────────────────────────────────────────────────────────────────
// CATALOG
// ─────────────────────────────────────────────────────────────────────────────

export const COMPONENT_TYPES: ComponentType[] = [

  // ── HERO / PORTADA ────────────────────────────────────────────────────────

  {
    id: 'BackgroundSection',
    label: 'Fondo Pantalla',
    category: 'hero',
    icon: '🎯',
    description: 'Sección con imagen de fondo a pantalla completa, título y descripción.',
    notes: 'Componente: src/components/Background/index.tsx. Props: backgroundImage, title, description.',
    fields: [
      { key: 'backgroundImage', type: 'image',    label: 'Imagen de fondo' },
      { key: 'title',           type: 'text',     label: 'Título' },
      { key: 'description',     type: 'textarea', label: 'Descripción' },
    ],
    defaultContent: { backgroundImage: '', title: '', description: '' },
  },
  {
    id: 'FullWidthBanner',
    label: 'Banner Ancho',
    category: 'hero',
    icon: '📢',
    description: 'Banner a ancho completo con logo y color de fondo configurable.',
    notes: 'Props: logoSrc, backgroundColor.',
    fields: [
      { key: 'logoSrc',         type: 'image', label: 'Logo' },
      { key: 'backgroundColor', type: 'text',  label: 'Color de fondo (CSS)', placeholder: '#ffffff' },
    ],
    defaultContent: { logoSrc: '', backgroundColor: '#ffffff' },
  },
  {
    id: 'HomeCover',
    label: 'Portada Inicio',
    category: 'hero',
    icon: '🏠',
    description: 'Portada de inicio.',
    fields: [
      { key: 'title',    type: 'text',  label: 'Título' },
      { key: 'subtitle', type: 'text',  label: 'Subtítulo' },
      { key: 'image',    type: 'image', label: 'Imagen' },
    ],
    defaultContent: { title: '', subtitle: '', image: '' },
  },

  // ── TEXTO ─────────────────────────────────────────────────────────────────

  {
    id: 'ElegantTextSection',
    label: 'Texto Elegante',
    category: 'text',
    icon: '✍️',
    description: 'Sección de texto sobre imagen de fondo con párrafos y botón CTA.',
    notes: 'Props: backgroundSrc, paragraphs[], buttonText, buttonLink.',
    fields: [
      { key: 'backgroundSrc', type: 'image',    label: 'Imagen de fondo' },
      { key: 'paragraphs',    type: 'textarea', label: 'Párrafos (uno por línea)' },
      { key: 'buttonText',    type: 'text',     label: 'Texto del botón' },
      { key: 'buttonLink',    type: 'url',      label: 'Enlace del botón' },
    ],
    defaultContent: { backgroundSrc: '', paragraphs: [''], buttonText: '', buttonLink: '' },
  },
  {
    id: 'PlainTextSection',
    label: 'Texto Simple',
    category: 'text',
    icon: '📄',
    description: 'Un texto destacado sobre una imagen de fondo, con botón opcional.',
    notes: 'Props: backgroundSrc, paragraphs[], buttonText, buttonLink, textColor.',
    fields: [
      { key: 'backgroundSrc', type: 'image',    label: 'Imagen de fondo' },
      { key: 'paragraphs',    type: 'textarea', label: 'Párrafos (uno por línea)' },
      { key: 'buttonText',    type: 'text',     label: 'Texto del botón' },
      { key: 'buttonLink',    type: 'url',      label: 'Enlace del botón' },
      { key: 'textColor',     type: 'text',     label: 'Color de texto (CSS)', placeholder: '#ffffff' },
    ],
    defaultContent: { backgroundSrc: '', paragraphs: [''], buttonText: '', buttonLink: '', textColor: '#ffffff' },
  },
  {
    id: 'InfoSection',
    label: 'Sección Info',
    category: 'text',
    icon: 'ℹ️',
    description: 'Sección informativa con imagen de fondo, imagen lateral, título, descripción y CTA.',
    notes: 'Props: backgroundSrc, title, description, imageSrc, imageAlt, buttonLink, buttonText.',
    fields: [
      { key: 'backgroundSrc', type: 'image',    label: 'Imagen de fondo' },
      { key: 'title',         type: 'text',     label: 'Título' },
      { key: 'description',   type: 'textarea', label: 'Descripción' },
      { key: 'imageSrc',      type: 'image',    label: 'Imagen lateral' },
      { key: 'imageAlt',      type: 'text',     label: 'Alt de la imagen' },
      { key: 'buttonText',    type: 'text',     label: 'Texto del botón' },
      { key: 'buttonLink',    type: 'url',      label: 'Enlace del botón' },
    ],
    defaultContent: { backgroundSrc: '', title: '', description: '', imageSrc: '', imageAlt: '', buttonText: '', buttonLink: '' },
  },
  {
    id: 'InfoPage',
    label: 'Página Info',
    category: 'text',
    icon: '📋',
    description: 'Página completa de información.',
    fields: [
      { key: 'title',       type: 'text',     label: 'Título' },
      { key: 'description', type: 'textarea', label: 'Descripción' },
      { key: 'image',       type: 'image',    label: 'Imagen' },
    ],
    defaultContent: { title: '', description: '', image: '' },
  },
  {
    id: 'HighlightSection',
    label: 'Destacado Derecha',
    category: 'text',
    icon: '⭐',
    description: 'Bloque destacado con imagen a la derecha y texto a la izquierda. Para resaltar algo importante.',
    notes: 'Props: imageSrc, title, description, buttonText, linkUrl, locale. IMPORTANTE: en COMPONENT_ORDER se identifica por el campo title[locale]. Puede haber varios HighlightSection en la misma sección.',
    fields: [
      { key: 'imageSrc',     type: 'image',    label: 'Imagen' },
      { key: 'title',        type: 'text',     label: 'Título' },
      { key: 'description',  type: 'textarea', label: 'Descripción' },
      { key: 'buttonText',   type: 'text',     label: 'Texto del botón' },
      { key: 'linkUrl',      type: 'url',      label: 'Enlace' },
    ],
    defaultContent: { imageSrc: '', title: '', description: '', buttonText: '', linkUrl: '' },
  },
  {
    id: 'HighlightLeft',
    label: 'Destacado Izquierda',
    category: 'text',
    icon: '⭐',
    description: 'Bloque destacado con imagen a la izquierda y texto a la derecha.',
    notes: 'Mismo patrón que HighlightSection pero imagen a la izquierda. Componente: src/components/HightlightLeft/index.tsx (typo en el nombre del directorio es intencional).',
    fields: [
      { key: 'imageSrc',    type: 'image',    label: 'Imagen' },
      { key: 'title',       type: 'text',     label: 'Título' },
      { key: 'description', type: 'textarea', label: 'Descripción' },
      { key: 'buttonText',  type: 'text',     label: 'Texto del botón' },
      { key: 'linkUrl',     type: 'url',      label: 'Enlace' },
    ],
    defaultContent: { imageSrc: '', title: '', description: '', buttonText: '', linkUrl: '' },
  },
  {
    id: 'FAQSection',
    label: 'Preguntas FAQ',
    category: 'text',
    icon: '❓',
    description: 'Lista de preguntas y respuestas plegables. Ideal para resolver dudas frecuentes.',
    notes: 'ID en COMPONENT_ORDER: "FAQSection". Componente importado como: import FAQSection from "@/components/Faq". Props: faqs[], imageUrl, title, description, linkText.',
    fields: [
      { key: 'title',       type: 'text',     label: 'Título' },
      { key: 'description', type: 'textarea', label: 'Descripción' },
      { key: 'imageUrl',    type: 'image',    label: 'Imagen' },
      { key: 'linkText',    type: 'text',     label: 'Texto enlace' },
      {
        key: 'faqs', type: 'array', label: 'Preguntas',
        itemTemplate: { question: '', answer: '' },
      },
    ],
    defaultContent: {
      title: '', description: '', imageUrl: '', linkText: '',
      faqs: [{ question: '', answer: '' }],
    },
  },
  {
    id: 'Features',
    label: 'Lista Características',
    category: 'text',
    icon: '✅',
    description: 'Lista de características con iconos. Usa featuresData.tsx por defecto.',
    fields: [
      { key: 'title',    type: 'text', label: 'Título' },
      { key: 'subtitle', type: 'text', label: 'Subtítulo' },
      {
        key: 'features', type: 'array', label: 'Características',
        itemTemplate: { icon: '', title: '', description: '' },
      },
    ],
    defaultContent: {
      title: '', subtitle: '',
      features: [{ icon: '', title: '', description: '' }],
    },
  },

  // ── GALERÍA ───────────────────────────────────────────────────────────────

  {
    id: 'Mosaic',
    label: 'Mosaico Imágenes',
    category: 'gallery',
    icon: '🧩',
    description: 'Mosaico de imágenes tipo galería para la portada.',
    notes: 'Este componente hace su propio fetch desde /api/content?section=HomeMosaic. Para editarlo crear/editar docs con section="HomeMosaic". Solo recibe prop locale.',
    selfFetching: true,
    fields: [
      { key: 'mainTitle',    type: 'text',  label: 'Título principal' },
      { key: 'subtitle',     type: 'text',  label: 'Subtítulo' },
      {
        key: 'cards', type: 'array', label: 'Tarjetas del mosaico',
        itemTemplate: { imgSrc: '', title: '', description: '', height: '', link: '' },
      },
    ],
    defaultContent: {
      mainTitle: '', subtitle: '',
      cards: [{ imgSrc: '', title: '', description: '', height: 'h-64', link: '' }],
    },
  },
  {
    id: 'PhotoGallery',
    label: 'Galería Fotos',
    category: 'gallery',
    icon: '📸',
    description: 'Galería de fotos en cuadrícula con lightbox.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'images', type: 'array', label: 'Imágenes',
        itemTemplate: { src: '', alt: '', caption: '' },
      },
    ],
    defaultContent: { title: '', images: [{ src: '', alt: '', caption: '' }] },
  },
  {
    id: 'ImageGrid',
    label: 'Grid Imágenes',
    category: 'gallery',
    icon: '🔲',
    description: 'Cuadrícula de imágenes con título, descripción y tarjetas.',
    notes: 'Props: title, description, cards[]. Cada card: imgSrc, title, description, height, link.',
    fields: [
      { key: 'title',       type: 'text',     label: 'Título' },
      { key: 'description', type: 'textarea', label: 'Descripción' },
      {
        key: 'cards', type: 'array', label: 'Tarjetas',
        itemTemplate: { imgSrc: '', title: '', description: '', height: 'h-64', link: '' },
      },
    ],
    defaultContent: {
      title: '', description: '',
      cards: [{ imgSrc: '', title: '', description: '', height: 'h-64', link: '' }],
    },
  },
  {
    id: 'Carousel',
    label: 'Carrusel Imágenes',
    category: 'gallery',
    icon: '🎠',
    description: 'Carrusel de imágenes con navegación.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'slides', type: 'array', label: 'Diapositivas',
        itemTemplate: { image: '', title: '', description: '' },
      },
    ],
    defaultContent: { title: '', slides: [{ image: '', title: '', description: '' }] },
  },

  // ── TARJETAS / GRID ───────────────────────────────────────────────────────

  {
    id: 'ElegantCardGrid',
    label: 'Tarjetas Elegantes',
    category: 'cards',
    icon: '🎴',
    description: 'Cuadrícula de tarjetas con diseño elegante.',
    notes: 'Props: title, cards[]. Cada card: imageSrc, title, description, link.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'cards', type: 'array', label: 'Tarjetas',
        itemTemplate: { imageSrc: '', title: '', description: '', link: '' },
      },
    ],
    defaultContent: {
      title: '',
      cards: [{ imageSrc: '', title: '', description: '', link: '' }],
    },
  },
  {
    id: 'CardGrid',
    label: 'Grid Tarjetas',
    category: 'cards',
    icon: '🃏',
    description: 'Cuadrícula estándar de tarjetas.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'cards', type: 'array', label: 'Tarjetas',
        itemTemplate: { image: '', title: '', description: '', link: '' },
      },
    ],
    defaultContent: { title: '', cards: [{ image: '', title: '', description: '', link: '' }] },
  },
  {
    id: 'RegionSlider',
    label: 'Slider Regiones',
    category: 'cards',
    icon: '🗺️',
    description: 'Slider de regiones geográficas.',
    notes: 'Props: regions[]. Cada region: image, headline, subtext.',
    fields: [
      {
        key: 'regions', type: 'array', label: 'Regiones',
        itemTemplate: { image: '', headline: '', subtext: '' },
      },
    ],
    defaultContent: {
      regions: [{ image: '', headline: '', subtext: '' }],
    },
  },
  {
    id: 'RegionsSection',
    label: 'Grid Regiones',
    category: 'cards',
    icon: '🌍',
    description: 'Listado visual de regiones.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'regions', type: 'array', label: 'Regiones',
        itemTemplate: { name: '', image: '', description: '', link: '' },
      },
    ],
    defaultContent: { title: '', regions: [{ name: '', image: '', description: '', link: '' }] },
  },
  {
    id: 'FourZones',
    label: 'Cuatro Zonas',
    category: 'cards',
    icon: '⊞',
    description: 'Grid de 4 zonas exactas.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'zones', type: 'array', label: 'Zonas (4)',
        itemTemplate: { image: '', title: '', description: '', link: '' },
      },
    ],
    defaultContent: {
      title: '',
      zones: [
        { image: '', title: '', description: '', link: '' },
        { image: '', title: '', description: '', link: '' },
        { image: '', title: '', description: '', link: '' },
        { image: '', title: '', description: '', link: '' },
      ],
    },
  },
  {
    id: 'TouristAttraction',
    label: 'Atracción Turística',
    category: 'cards',
    icon: '🏛️',
    description: 'Ficha de un lugar de interés: imagen, descripción y enlace a su web.',
    fields: [
      { key: 'name',        type: 'text',     label: 'Nombre' },
      { key: 'description', type: 'textarea', label: 'Descripción' },
      { key: 'image',       type: 'image',    label: 'Imagen' },
      { key: 'location',    type: 'text',     label: 'Ubicación' },
      { key: 'mapUrl',      type: 'url',      label: 'URL mapa' },
    ],
    defaultContent: { name: '', description: '', image: '', location: '', mapUrl: '' },
  },
  {
    id: 'TransportDetailSection',
    label: 'Info Transporte',
    category: 'cards',
    icon: '🚌',
    description: 'Opciones de transporte disponibles.',
    fields: [
      { key: 'title',       type: 'text',     label: 'Título' },
      { key: 'description', type: 'textarea', label: 'Descripción' },
      {
        key: 'options', type: 'array', label: 'Opciones',
        itemTemplate: { type: '', description: '', icon: '' },
      },
    ],
    defaultContent: { title: '', description: '', options: [{ type: '', description: '', icon: '' }] },
  },
  {
    id: 'Testimonials',
    label: 'Testimonios',
    category: 'cards',
    icon: '💬',
    description: 'Testimonios de usuarios.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'testimonials', type: 'array', label: 'Testimonios',
        itemTemplate: { name: '', role: '', text: '', avatar: '' },
      },
    ],
    defaultContent: { title: '', testimonials: [{ name: '', role: '', text: '', avatar: '' }] },
  },
  {
    id: 'Pricing',
    label: 'Precios Planes',
    category: 'cards',
    icon: '💰',
    description: 'Planes y precios.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'plans', type: 'array', label: 'Planes',
        itemTemplate: { name: '', price: '', period: '', features: '', ctaText: '', ctaLink: '' },
      },
    ],
    defaultContent: { title: '', plans: [{ name: '', price: '', period: '', features: '', ctaText: '', ctaLink: '' }] },
  },
  {
    id: 'Brands',
    label: 'Carrusel Logos',
    category: 'cards',
    icon: '🏷️',
    description: 'Logos de marcas. Usa brandsData.tsx por defecto.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      { key: 'brands', type: 'array', label: 'Marcas', itemTemplate: { name: '', logo: '', link: '' } },
    ],
    defaultContent: { title: '', brands: [{ name: '', logo: '', link: '' }] },
  },
  {
    id: 'SponsorLogos',
    label: 'Logos Patrocinadores',
    category: 'cards',
    icon: '🤝',
    description: 'Logos de patrocinadores.',
    fields: [
      { key: 'sponsors', type: 'array', label: 'Patrocinadores', itemTemplate: { name: '', logo: '', link: '' } },
    ],
    defaultContent: { sponsors: [{ name: '', logo: '', link: '' }] },
  },
  {
    id: 'WebLinkGrid',
    label: 'Grid Enlaces',
    category: 'cards',
    icon: '🔗',
    description: 'Cuadrícula de enlaces externos.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'links', type: 'array', label: 'Enlaces',
        itemTemplate: { label: '', url: '', icon: '', description: '' },
      },
    ],
    defaultContent: { title: '', links: [{ label: '', url: '', icon: '', description: '' }] },
  },

  // ── ARTÍCULOS ─────────────────────────────────────────────────────────────

  {
    id: 'ArticleGrid',
    label: 'Grid Artículos',
    category: 'articles',
    icon: '📰',
    description: 'Rejilla de artículos con imagen y resumen. Para listar noticias o contenidos.',
    notes: 'ID en COMPONENT_ORDER: "ArticleGrid". Importado como: import ArticleGrid from "@/components/Articles". Props: mainTitle, subtitle, ctaText, articles[].',
    fields: [
      { key: 'mainTitle', type: 'text', label: 'Título principal' },
      { key: 'subtitle',  type: 'text', label: 'Subtítulo' },
      { key: 'ctaText',   type: 'text', label: 'Texto CTA' },
      {
        key: 'articles', type: 'array', label: 'Artículos',
        itemTemplate: { title: '', summary: '', image: '', date: '', slug: '' },
      },
    ],
    defaultContent: {
      mainTitle: '', subtitle: '', ctaText: '',
      articles: [{ title: '', summary: '', image: '', date: '', slug: '' }],
    },
  },
  {
    id: 'ArticlesSlider',
    label: 'Slider Artículos',
    category: 'articles',
    icon: '📜',
    description: 'Carrusel de artículos o regiones con imagen.',
    notes: 'Props: regions[]. Cada region: image, name, description.',
    fields: [
      {
        key: 'regions', type: 'array', label: 'Regiones/Artículos',
        itemTemplate: { image: '', name: '', description: '' },
      },
    ],
    defaultContent: { regions: [{ image: '', name: '', description: '' }] },
  },
  {
    id: 'NewsHome',
    label: 'Noticias Destacadas',
    category: 'articles',
    icon: '📰',
    description: 'Noticias destacadas para la home.',
    notes: 'Props: title, newsData[]. Cada item: image, title, description, socialLinks[].',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'newsData', type: 'array', label: 'Noticias',
        itemTemplate: { image: '', title: '', description: '' },
      },
    ],
    defaultContent: {
      title: '',
      newsData: [{ image: '', title: '', description: '' }],
    },
  },
  {
    id: 'News',
    label: 'Lista Noticias',
    category: 'articles',
    icon: '📡',
    description: 'Listado de noticias.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'newsData', type: 'array', label: 'Noticias',
        itemTemplate: { image: '', title: '', description: '' },
      },
    ],
    defaultContent: { title: '', newsData: [{ image: '', title: '', description: '' }] },
  },
  {
    id: 'Article',
    label: 'Artículo Individual',
    category: 'articles',
    icon: '📄',
    description: 'Artículo individual.',
    fields: [
      { key: 'title',   type: 'text',     label: 'Título' },
      { key: 'date',    type: 'text',     label: 'Fecha' },
      { key: 'image',   type: 'image',    label: 'Imagen' },
      { key: 'content', type: 'textarea', label: 'Contenido' },
      { key: 'author',  type: 'text',     label: 'Autor' },
    ],
    defaultContent: { title: '', date: '', image: '', content: '', author: '' },
  },
  {
    id: 'FullArticleRestyling',
    label: 'Artículo Completo',
    category: 'articles',
    icon: '📖',
    description: 'Artículo largo a página completa, con secciones de texto e imágenes.',
    notes: 'Props: title, readingTime, mainImage, mainImageCredit, sections[], videoId.',
    fields: [
      { key: 'title',           type: 'text',     label: 'Título' },
      { key: 'readingTime',     type: 'text',     label: 'Tiempo de lectura' },
      { key: 'mainImage',       type: 'image',    label: 'Imagen portada' },
      { key: 'mainImageCredit', type: 'text',     label: 'Crédito imagen' },
      { key: 'videoId',         type: 'text',     label: 'ID vídeo Vimeo (opcional)' },
      {
        key: 'sections', type: 'array', label: 'Secciones',
        itemTemplate: { title: '', content: '', image: '' },
      },
    ],
    defaultContent: {
      title: '', readingTime: '', mainImage: '', mainImageCredit: '', videoId: '',
      sections: [{ title: '', content: '', image: '' }],
    },
  },
  {
    id: 'FullArticle',
    label: 'Artículo Página',
    category: 'articles',
    icon: '📖',
    description: 'Artículo a página completa (versión original).',
    fields: [
      { key: 'title',      type: 'text',     label: 'Título' },
      { key: 'date',       type: 'text',     label: 'Fecha' },
      { key: 'coverImage', type: 'image',    label: 'Imagen portada' },
      { key: 'intro',      type: 'textarea', label: 'Introducción' },
      { key: 'body',       type: 'textarea', label: 'Cuerpo' },
      { key: 'author',     type: 'text',     label: 'Autor' },
    ],
    defaultContent: { title: '', date: '', coverImage: '', intro: '', body: '', author: '' },
  },
  {
    id: 'IndependentArticle',
    label: 'Artículo Independiente',
    category: 'articles',
    icon: '📑',
    description: 'Artículo sin dependencia de sección padre.',
    fields: [
      { key: 'title',   type: 'text',     label: 'Título' },
      { key: 'image',   type: 'image',    label: 'Imagen' },
      { key: 'content', type: 'textarea', label: 'Contenido' },
    ],
    defaultContent: { title: '', image: '', content: '' },
  },

  // ── MULTIMEDIA ────────────────────────────────────────────────────────────

  {
    id: 'VimeoWithText',
    label: 'Vimeo Texto',
    category: 'media',
    icon: '🎬',
    description: 'Vídeo de Vimeo con texto al lado (admite negrita, enlaces…).',
    notes: 'Props: videoUrl, text. videoUrl es la URL completa de Vimeo.',
    fields: [
      { key: 'videoUrl', type: 'url',      label: 'URL del vídeo Vimeo', placeholder: 'https://vimeo.com/...' },
      { key: 'text',     type: 'textarea', label: 'Texto lateral' },
    ],
    defaultContent: { videoUrl: '', text: '' },
  },
  {
    id: 'ReusableVimeoVideo',
    label: 'Video Vimeo',
    category: 'media',
    icon: '▶️',
    description: 'Componente Vimeo reutilizable. Componente VideoVimeoInsert en vida_pet.',
    notes: 'ID en COMPONENT_ORDER: "ReusableVimeoVideo". Importado como: import ReusableVimeoVideo from "@/components/VideoVimeoInsert".',
    fields: [
      { key: 'videoId', type: 'text', label: 'ID del vídeo Vimeo' },
    ],
    defaultContent: { videoId: '' },
  },
  {
    id: 'Vimeo',
    label: 'Vimeo Standalone',
    category: 'media',
    icon: '🎥',
    description: 'Componente Vimeo standalone.',
    notes: 'Props: videoUrl, showHeader, isSubPage.',
    fields: [
      { key: 'videoUrl',   type: 'url',     label: 'URL del vídeo Vimeo' },
      { key: 'showHeader', type: 'boolean', label: 'Mostrar cabecera' },
      { key: 'isSubPage',  type: 'boolean', label: 'Es subpágina' },
    ],
    defaultContent: { videoUrl: '', showHeader: false, isSubPage: false },
  },
  {
    id: 'VideoYoutubeInsert',
    label: 'Vídeo YouTube',
    category: 'media',
    icon: '▶️',
    description: 'Insertar un vídeo de YouTube.',
    fields: [
      { key: 'title',       type: 'text',     label: 'Título' },
      { key: 'videoId',     type: 'text',     label: 'ID del vídeo YouTube' },
      { key: 'description', type: 'textarea', label: 'Descripción' },
    ],
    defaultContent: { title: '', videoId: '', description: '' },
  },

  // ── INTERACTIVO ───────────────────────────────────────────────────────────

  {
    id: 'Contact',
    label: 'Contacto',
    category: 'interactive',
    icon: '📬',
    description: 'Sección de contacto con información y formulario. Datos hardcoded en componente.',
    notes: 'Este componente no recibe props de contenido, usa traducciones inline con el prop locale.',
    fields: [
      { key: 'address', type: 'text', label: 'Dirección' },
      { key: 'phone',   type: 'text', label: 'Teléfono' },
      { key: 'email',   type: 'text', label: 'Email' },
      { key: 'mapUrl',  type: 'url',  label: 'URL del mapa' },
    ],
    defaultContent: { address: '', phone: '', email: '', mapUrl: '' },
  },
  {
    id: 'WeatherComponent',
    label: 'Widget Tiempo',
    category: 'interactive',
    icon: '☀️',
    description: 'Widget meteorológico. Solo recibe prop locale.',
    notes: 'No gestiona contenido CMS, solo prop locale.',
    fields: [
      { key: 'city', type: 'text', label: 'Ciudad' },
    ],
    defaultContent: { city: '' },
  },

  // ── LAYOUT ────────────────────────────────────────────────────────────────

  {
    id: 'About',
    label: 'Sobre Nosotros',
    category: 'layout',
    icon: '🏢',
    description: 'Sección "Sobre nosotros". Usa traducciones inline con locale.',
    fields: [
      { key: 'title',       type: 'text',     label: 'Título' },
      { key: 'description', type: 'textarea', label: 'Descripción' },
      { key: 'image',       type: 'image',    label: 'Imagen' },
    ],
    defaultContent: { title: '', description: '', image: '' },
  },
  {
    id: 'Blog',
    label: 'Blog / Posts',
    category: 'layout',
    icon: '✏️',
    description: 'Listado de posts. Usa blogData.tsx por defecto.',
    fields: [
      { key: 'title', type: 'text', label: 'Título' },
      {
        key: 'posts', type: 'array', label: 'Posts',
        itemTemplate: { title: '', summary: '', image: '', date: '', slug: '' },
      },
    ],
    defaultContent: { title: '', posts: [{ title: '', summary: '', image: '', date: '', slug: '' }] },
  },
  {
    id: 'CenteredLink',
    label: 'Enlace Centrado',
    category: 'layout',
    icon: '🔗',
    description: 'Botón o enlace centrado.',
    fields: [
      { key: 'text', type: 'text', label: 'Texto' },
      { key: 'href', type: 'url',  label: 'URL' },
    ],
    defaultContent: { text: '', href: '' },
  },
  {
    id: 'SeeMore',
    label: 'Ver Más',
    category: 'layout',
    icon: '👁️',
    description: 'Botón "Ver más".',
    fields: [
      { key: 'text', type: 'text', label: 'Texto' },
      { key: 'href', type: 'url',  label: 'URL' },
    ],
    defaultContent: { text: 'Ver más', href: '' },
  },
  {
    id: 'LegalDisclaimer',
    label: 'Aviso Legal',
    category: 'layout',
    icon: '⚖️',
    description: 'Texto de aviso legal.',
    fields: [
      { key: 'title',   type: 'text',     label: 'Título' },
      { key: 'content', type: 'textarea', label: 'Contenido legal' },
    ],
    defaultContent: { title: '', content: '' },
  },
  {
    id: 'CookieBanner',
    label: 'Banner Cookies',
    category: 'layout',
    icon: '🍪',
    description: 'Banner de cookies. Usa traducciones inline.',
    fields: [
      { key: 'message',     type: 'textarea', label: 'Mensaje' },
      { key: 'acceptText',  type: 'text',     label: 'Texto aceptar' },
      { key: 'declineText', type: 'text',     label: 'Texto rechazar' },
      { key: 'policyLink',  type: 'url',      label: 'Enlace política' },
    ],
    defaultContent: { message: '', acceptText: 'Aceptar', declineText: 'Rechazar', policyLink: '' },
  },

  // ── NAVEGACIÓN ────────────────────────────────────────────────────────────

  {
    id: 'Header',
    label: 'Cabecera Menú',
    category: 'navigation',
    icon: '🔝',
    description: 'Cabecera con menú. Datos del menú en menuData.tsx.',
    notes: 'Recibe prop subPage (boolean). El menú se configura en src/components/Header/menuData.tsx.',
    fields: [
      { key: 'logoUrl', type: 'image', label: 'Logo' },
      { key: 'logoAlt', type: 'text',  label: 'Alt del logo' },
    ],
    defaultContent: { logoUrl: '', logoAlt: '' },
  },
  {
    id: 'Footer',
    label: 'Pie Página',
    category: 'navigation',
    icon: '🔚',
    description: 'Pie de página. Usa traducciones inline con locale.',
    fields: [
      { key: 'copyright', type: 'text', label: 'Texto copyright' },
      {
        key: 'links', type: 'array', label: 'Enlace rápidos',
        itemTemplate: { label: '', href: '' },
      },
      {
        key: 'socialLinks', type: 'array', label: 'Redes sociales',
        itemTemplate: { platform: '', url: '', icon: '' },
      },
    ],
    defaultContent: {
      copyright: '',
      links: [{ label: '', href: '' }],
      socialLinks: [{ platform: '', url: '', icon: '' }],
    },
  },
  {
    id: 'LanguageSelector',
    label: 'Selector Idioma',
    category: 'navigation',
    icon: '🌐',
    description: 'Selector de idioma (ES/EN).',
    fields: [
      {
        key: 'languages', type: 'array', label: 'Idiomas',
        itemTemplate: { code: '', label: '' },
      },
    ],
    defaultContent: {
      languages: [{ code: 'es', label: 'Español' }, { code: 'en', label: 'English' }],
    },
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getComponentsByCategory(category: ComponentCategory): ComponentType[] {
  return COMPONENT_TYPES.filter((c) => c.category === category);
}

export function getComponentType(id: string): ComponentType | undefined {
  return COMPONENT_TYPES.find((c) => c.id === id);
}

/**
 * IDs of components that appear in vida_pet's main COMPONENT_ORDER (home/dynamic pages).
 * These are the most commonly used in the page builder.
 */
export const MAIN_PAGE_COMPONENT_IDS = [
  'VimeoWithText',
  'FullWidthBanner',
  'FAQSection',
  'ReusableVimeoVideo',
  'Mosaic',
  'HighlightSection',
  'HighlightLeft',
  'ElegantCardGrid',
  'RegionSlider',
  'ArticleGrid',
  'NewsHome',
  'BackgroundSection',
];
